'use client'

import {
    Box,
    Grid,
    Icon,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {motion} from "framer-motion";
import {Image} from "primereact/image";
import Error from "@/components/shared/error";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRenewPasswordMutation} from "@/redux/rtk/login/login";
import OtpInput from "react-otp-input";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomSnackbar from "@/components/snackbar/customSnackbar";
import {useRouter} from "next/navigation";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {settingsSelectors, updateGlobalLoading} from "@/redux/slices/settings";
import {FIELDS, NEW_PASSWORD, PASSWORD_ERROR, SERVER_ERROR_PROPS} from "@/components/admin/login/types";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {validatePassword} from "@/components/shared/client/utils";
import {defaultLoginBackgroundImage} from "@/components/constants";
import {GENERAL_OBJECT} from "@/components/types";

const button = {
    borderRadius: 2,
    py: 1.5,
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 600,
    boxShadow: "0 8px 16px rgba(0, 13, 255, 0.2)"
}

const initialValue: NEW_PASSWORD = {
    password_1: "",
    password_2: "",
    activationCode: "",
    email: ""
}

const NewPassword = ({setStep, fields, setFields, isAdmin = false}: FIELDS) => {
    const siteImages = useSelector((state) => settingsSelectors.getSiteImages(state), shallowEqual);
    const dispatch = useDispatch();
    const router = useRouter();
    const theme = useTheme();
    const {t} = useTranslation("login");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [err, setErr] = useState<SERVER_ERROR_PROPS | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [rem, setRem] = useState<number>(0);
    const [isInputError, setIsInputError] = useState<boolean>(true);
    const [handleResetPassword, {data, error, isLoading}] = useRenewPasswordMutation();
    const [showPassword, setShowPassword] = useState<GENERAL_OBJECT>({
        password_1: false,
        password_2: false,
    })
    const [passwordError, setPasswordError] = useState<PASSWORD_ERROR>({
        length: true,
        upperCase: true,
        number: true,
        special: true
    })
    const [formData, setFormData] = useState<NEW_PASSWORD>({
        ...initialValue,
        email: fields.email ?? ""
    });
    const [errors, setErrors] = useState<NEW_PASSWORD>(initialValue);
    const passwordRef1 = useRef(null);
    const passwordRef2 = useRef(null);

    const {theme: selectedTheme, webContext} = useSelector((state) => {
        const webContext = settingsSelectors.getWebContext(state);
        const theme = settingsSelectors.getTheme(state);
        return {webContext, theme};
    }, shallowEqual);

    useEffect(() => {
        setIsInputError(validateFields())
    }, [formData])

    const handleData = (data: any) => {
        const {code, redirectTo} = data?.data || {};
        if (code === 200) {
            if (redirectTo) {
                dispatch(updateGlobalLoading(true));
                router.replace(redirectTo);
            }
            return;
        }
        if (code === 301) {
            dispatch(updateGlobalLoading(true));
            setSuccessMessage(t("activate.passwordHasBeenSuccessfullyUpdated" as any));
            location.reload()
        }
    };

    useEffect(() => {
        handleData(data)
    }, [data]);

    useEffect(() => {
        // @ts-ignore
        if (error?.message) setErr({message: error.message, localesData: error?.localesData})
    }, [error]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value, checked} = e.target
        setFormData({
            ...formData,
            [name]: name === "rememberMe" ? checked : value,
        });
        if (name === "password_1") {
            setPasswordError(validatePassword(value));
        }
        if (errors[name as keyof typeof errors]) {
            setErrors({
                ...errors,
                [name]: "",
            })
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        void handleResetPassword({
            email: formData?.email,
            activationCode: formData?.activationCode,
            password: formData?.password_1
        })
    }

    const handleClose = () => {
        setSuccessMessage("")
    }

    const handleTogglePasswordVisibility = (type: number) => {
        const input = type === 1 ? passwordRef1.current : passwordRef2.current;
        const {selectionStart, selectionEnd} = input!;
        setShowPassword(state => ({
            ...state,
            [`password_${type}`]: !showPassword[`password_${type}` ]
        }))
        setTimeout(() => {
            // @ts-ignore
            input!.setSelectionRange(selectionStart, selectionEnd);
        }, 0);
    }

    const handleMouseDownPassword = (event: React.MouseEvent) => {
        event.preventDefault();
    };

    const handleCodeChange = (newValue: string) => {
        setFormData(state => ({
            ...state,
            activationCode:newValue,
        }));
        setErrors(state => ({
            ...state,
            activationCode: "",
        }));
    }

    const isPasswordMatch = (): boolean => {
        return !!formData?.password_1 && (formData?.password_1 === formData?.password_2)
    }

    const validateFields = () => {
        if(!formData?.password_1) return true;
        if(!formData?.password_2) return true;
        if(Object.values(passwordError).some(value => value)) return true;
        if(!isPasswordMatch()) return true;
        return (!formData?.activationCode || formData.activationCode.length < 6);

    }

    return <Grid container className="df jcc aic we1 mhe1 hfc">

        <CustomSnackbar open={!!successMessage} handleClose={handleClose}
                        message={successMessage}/>

        <Grid
            size={{xs: false, md: 6, lg: 8}}
            className="df jcc aic"
            sx={{
                alignSelf: "stretch",
                ...!isMobile ? {
                    backgroundImage: siteImages?.images?.[`${isAdmin ? 'loginImageAdmin' : 'loginImage'}`] ? `url(${siteImages.dir}${siteImages.images[`${isAdmin ? 'loginImageAdmin' : 'loginImage'}`]}1.webp)` : defaultLoginBackgroundImage,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundColor: (theme) => theme.palette?.backgrounds?.secondary
                } : {}
            }}
        >
        </Grid>
        <Grid className="jcc aic df" size={{xs: 12, md: 6, lg: 4}}
               sx={{background: theme => theme.palette?.backgrounds?.primary}}>

            <Grid container sx={{
                width: {
                    xs: "100%",
                    sm: "30rem",
                },
                background: theme => theme.palette?.backgrounds?.primary
            }}>
                <Box
                    className="he1 df fdc aic jcc we1"
                    sx={{
                        borderRadius: "10px",
                        p: 4
                    }}
                >
                    <motion.div
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{duration: 0.5}}
                        style={{width: "100%"}}
                    >
                        {(webContext?.logo_1_url && webContext?.logo_2_url) ?
                            <Box className="df jcc">
                                <Image
                                    src={selectedTheme === 1 ? `${webContext.logo_2_url}1.webp` : `${webContext.logo_1_url}1.webp`}
                                    alt="Company Logo"
                                    width={"150px"}
                                />
                            </Box> : null}
                        <Box sx={{mb: 1, mt: 4}}>
                            <Typography
                                variant="h4"
                                component="h1"
                                fontWeight="bold"
                                sx={{color: (theme: Theme) => theme.palette?.texts?.main, mb: 1, mt: 2}
                                }>
                                {t('newPassword')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('newPasswordInstructions')}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} sx={{mb: 4}}>
                            <Typography variant="body2">{t('global:email')}</Typography>
                            <Typography color="success" sx={{fontWeight: "bold"}} variant="body2">{fields.email}</Typography>
                        </Stack>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>

                                <OtpInput
                                    value={formData.activationCode}
                                    onChange={handleCodeChange}
                                    numInputs={6}
                                    renderSeparator={() => <span>-</span>}
                                    renderInput={(props) => <TextField {...props} />}
                                />

                                <TextField
                                    fullWidth
                                    label={t('newPassword')}
                                    name="password_1"
                                    type={!showPassword?.password_1 ? "password" : "text"}
                                    variant="outlined"
                                    value={formData.password_1}
                                    onChange={handleChange}
                                    inputRef={passwordRef1}
                                    InputProps={{
                                        sx: {borderRadius: 2},
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => handleTogglePasswordVisibility(1)}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword?.password_1 ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label={t('reTypePassword')}
                                    name="password_2"
                                    type={!showPassword?.password_2 ? "password" : "text"}
                                    variant="outlined"
                                    value={formData.password_2}
                                    onChange={handleChange}
                                    inputRef={passwordRef2}
                                    InputProps={{
                                        sx: {borderRadius: 2},
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => handleTogglePasswordVisibility(2)}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword?.password_2 ? <VisibilityOff/> : <Visibility/>}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Grid container className="df we1" sx={{flexDirection: {xs: "column", md: "row"}}}>
                                    <Grid className="df flex5" sx={{p: 0.2}}>
                                        <LoadingButton
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={isLoading}
                                            onClick={() => setStep(2)}
                                            startIcon={<ChevronLeftIcon/>}
                                            sx={{
                                                ...button
                                            }}
                                        >
                                            {t("global:back")}
                                        </LoadingButton>
                                    </Grid>
                                    <Grid className="df flex5" sx={{p: 0.2}}>
                                        <LoadingButton
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            disabled={isInputError}
                                            loading={isLoading}
                                            sx={{
                                                ...button
                                            }}
                                        >
                                            {t('global:submit')}
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </form>

                        {(!!err) &&
                            <Error error={err} timeout={10000}/>}

                        <Grid container sx={{mt: 2}}>
                            <Stack direction="column" spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Icon
                                        color={!passwordError?.length ? "success" : "error"}
                                        sx={{fontSize: "1.2rem!important"}}
                                    >
                                        {!passwordError.length ? "check_circle" : "info_icon"}
                                    </Icon>
                                    <Typography variant="body2">{t('passwords.type_1')}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Icon
                                        color={!passwordError?.upperCase ? "success" : "error"}
                                        sx={{fontSize: "1.2rem!important"}}
                                    >
                                        {!passwordError.upperCase ? "check_circle" : "info_icon"}
                                    </Icon>
                                    <Typography variant="body2">{t('passwords.type_2')}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Icon
                                        color={!passwordError?.number ? "success" : "error"}
                                        sx={{fontSize: "1.2rem!important"}}
                                    >
                                        {!passwordError.number ? "check_circle" : "info_icon"}
                                    </Icon>
                                    <Typography variant="body2">{t('passwords.type_3')}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Icon
                                        color={!passwordError?.special ? "success" : "error"}
                                        sx={{fontSize: "1.2rem!important"}}
                                    >
                                        {!passwordError.special ? "check_circle" : "info_icon"}
                                    </Icon>
                                    <Typography variant="body2">{t('passwords.type_4')}</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Icon
                                        color={isPasswordMatch() ? "success" : "error"}
                                        sx={{fontSize: "1.2rem!important"}}
                                    >
                                        {isPasswordMatch() ? "check_circle" : "info_icon"}
                                    </Icon>
                                    <Typography variant="body2">{t('passwords.type_5')}</Typography>
                                </Stack>
                            </Stack>
                        </Grid>

                        <Grid container className="df jcc" sx={{mt: 2}}>
                            <Grid>{t('alreadyHaveAnAccount')}</Grid>
                            <Grid
                                className="fwb cp"
                                sx={{ml: 0.5, mr: 0.5, color: "primary.main"}}
                                onClick={() => setStep!(1)}
                            >
                                {t('signIn')}
                            </Grid>
                        </Grid>

                    </motion.div>
                </Box>
            </Grid>

        </Grid>
    </Grid>
}

export default NewPassword;